import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { AssessmentEdit } from '../model/assessment.edit';
import { State, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { AssessmentListService } from 'src/app/core/service/grid-data/grid-data-assessment.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { saveAs } from '@progress/kendo-file-saver';
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { AssessmentExportComponent } from 'src/app/shared/assessment-export/assessment-export.component';
import { Base64Util } from 'src/app/core/utility/base64-util';
import { Base64 } from 'js-base64';

@Component({
  selector: 'app-assessment-grid',
  templateUrl: './assessment-grid.component.html',
  styleUrls: ['./assessment-grid.component.scss']
})
export class AssessmentGridComponent implements OnInit
{

  /**
   * Indicate which grid to render.
   */
  @Input() public archived = false;

  @Output() public refreshAssessmentTotal = new EventEmitter<number>();

  @ViewChild('exportAssessment') public exportAssessment: AssessmentExportComponent;

  public pageSizes = [5, 10, 25, 50, 100];
  public enableAdvanced = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public isNew = false;
  public editDataItem: AssessmentEdit;
  public selectedAssessment: AssessmentEdit;
  public deleteDialogOpened = false;

  /**
   * Assessment list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public filterText = '';

  constructor(
    private assessmentListService: AssessmentListService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private assessmentService: AssessmentService,
    private messageHandlerService: MessageHandlerService) { }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.assessmentListService.loading;
  }

  public ngOnInit(): void
  {
    this.searchFieldChange('');
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.queryAssessment(state);
  }

  /**
   * Filtering by search field
   */
  public filterChange(currentFilter: CompositeFilterDescriptor): void
  {
    this.state.filter = currentFilter;
    this.queryAssessment(this.state);
  }

  /**
   * Search has been changed.
   *
   * @param filterText Search text.
   */
  public searchFieldChange(filterText: string): void
  {
    this.setState();
    if (filterText !== '')
    {
      const root = this.searchHelperService.fieldChange(filterText, this.state, 'name');
      this.filterText = filterText;
      this.filterChange(root);
    }
    else
    {
      this.filterChange(this.state.filter);
    }
  }

  /**
   * Initialize the proper state before query
   */
  public setState(): void
  {
    this.state = {
      filter: {
        filters: [
          { field: 'deleted', operator: 'eq', value: this.archived },
        ], logic: 'and'
      },
      group: [],
      skip: 0,
      sort: [
        { field: 'name', dir: 'asc' }
      ],
      take: this.appConfigService.getConfig().pageSize
    };
  }

  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new AssessmentEdit();
    });
  }

  /**
   * Provide the advanced mode storage key based on the grid context.
   *
   * @param isArchived True if this is the archived grid.
   */
  public getAdvancedGridStorageKey(isArchived: boolean): string
  {
    return isArchived ? 'assessments-list-archived' : 'assessments-list';
  }

  /**
   * Save document to local
   *
   * @param url Document url
   * @param name Document file name
   */
  public saveFileLocal(id: number, name: string): void
  {
    this.assessmentService.getAssessment(id).subscribe({
      next: (data: any) =>
      {
        if (data.fields !== null)
        {
          const fields = JSON.parse(Base64Util.decodeUnicode(data.fields));
          data.fields = fields;
        }
        const based64Data = Base64.encode(JSON.stringify(data));
        const fileName = name.replace(/ /g, '_');
        saveAs(`data:application/json;base64,${based64Data}`, `${fileName}.json`);
      }
    });
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.assessmentService.inUseByModeler(id).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('ASSESSMENT-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.assessmentService.getAssessment(id).subscribe({
            next: (data) =>
            {
              this.selectedAssessment = data as AssessmentEdit;
              this.deleteDialogOpened = true;
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public deleteAssessment(): void
  {
    // Proceed with the deletion
    this.assessmentService.deleteAssessment(this.selectedAssessment.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('ASSESSMENT-DELETED-SUCCESSFULLY');
        this.gridView.data = this.gridView.data.filter(e => e.id !== this.selectedAssessment.id);
        // Reload entire grid if there is no more record to display on a page.
        if (this.gridView.data.length === 0 && this.state.skip > 0)
        {
          this.state.skip = 0;
          this.queryAssessment(this.state);
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.deleteDialogOpened = false;
      }
    });
  }

  /**
   * Save Assessment change.
   *
   * @param assessment Assessment object.
   */
  public saveChangesHandler(assessment: AssessmentEdit): void
  {
    this.assessmentService.saveAssessment(assessment, this.isNew, assessment.id).subscribe({
      next: () =>
      {
        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('ASSESSMENT-SAVED-SUCCESSFULLY');

        this.state.skip = 0;
        this.queryAssessment(this.state);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.assessmentService.getAssessment(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Export the completed assessements.
   *
   * @param id Assessment id
   */
  public exportCompletedAssessments(id: number, assessmentVersion: string, assessmentName: string): void
  {
    this.exportAssessment.execute(id, assessmentVersion, assessmentName);
  }

  /**
   * Query the assessment list. Setting isAdmin=true to make sure we load only the assessment that are public or for users listed
   * in the staff list. If current logged in used is equal to CreatedBy or LastModifiedBy, assessment will be loaded.
   *
   * @param state Current Assessment state object.
   */
  private queryAssessment(state: State): void
  {
    this.assessmentListService.queryByState(state, (assessments: any) =>
    {
      // Preprocessing data before it's passed to the Kendo Grid.
      if (assessments !== null)
      {
        assessments.data.forEach(d =>
        {
          d.createdOn = new Date(d.createdOn);
          d.lastModifiedOn = new Date(d.lastModifiedOn);
        });
      }
      this.gridView = {
        data: (assessments) ? this.gridData = assessments.data : this.gridData = [],
        total: (assessments) ? this.total = assessments.total : this.total = 0
      };
      this.refreshAssessmentTotal.emit(this.total);
    }, '&isAdmin=true');
  }
}
